/**
 * Login — /login route.
 *
 * Arjun's form IS the auth UI. Two flow shapes (mirror lib/auth.tsx):
 *
 *  - **OIDC configured**: the SPA is the configured
 *    ZITADEL_OIDC_DEFAULTLOGINURLV2. If we land here without
 *    ?authRequest, fire initiateOidcRedirect() once so Zitadel mints
 *    one and 302s back to /login?authRequest=…. Then render the form;
 *    submit POSTs to /v1/platform/auth/login (the SPA-side proxy in
 *    platform-core/internal/loginproxy). On success, navigate the
 *    window to the returned callbackUrl, which lands on /auth/callback
 *    where the PKCE code exchange completes.
 *
 *  - **Stub mode** (no VITE_OIDC_*): no authRequest needed; submit
 *    triggers the synthetic stub signIn and navigates to /app.
 *
 * Google / Apple buttons are real federation links — they deep-link into
 * Zitadel's hosted login (see lib/idp-login.ts) and only render when the
 * provider's IdP id is configured (VITE_GOOGLE_IDP_ID / VITE_APPLE_IDP_ID,
 * produced by scripts/register-zitadel-idps.sh). No ids ⇒ no buttons.
 * See docs/runbooks/zitadel-idp-setup.md + docs/decisions/2026-05-30_spa-owns-login-ui.md.
 */

import { useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  clearReturnTo,
  isOwnAuthRequest,
  rememberOwnAuthRequest,
  rememberReturnTo,
  safeAppPath,
} from "@/lib/nav/return-to";
import { authErrorMessage } from "@/lib/auth-errors";
import { configuredIdps, startIdpLogin } from "@/lib/idp-login";
import { isOidcConfigured } from "@/lib/oidc-config";
import { useTenantTheme } from "@/lib/tenant-theme";
import { useToast } from "@/lib/ui/toast";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { Separator } from "@/lib/ui/separator";

interface LocationState {
  from?: { pathname: string; search?: string; hash?: string };
}

export function Login() {
  const { signIn, initiateOidcRedirect, isAuthenticated, isLoading } =
    useAuth();
  const tenant = useTenantTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { push: pushToast } = useToast();
  const [searchParams] = useSearchParams();
  const authRequestId = searchParams.get("authRequest");
  const fromState = (location.state as LocationState | null)?.from;
  // The page the person had open, with its query: sign-in returns there (Navigation review, D11), across the OIDC round
  // trip too, which loses the router's state (lib/nav/landing.ts; /app reads it once).
  const from = safeAppPath(fromState ? fromState.pathname + (fromState.search ?? "") + (fromState.hash ?? "") : null) ?? "/app";
  useEffect(() => {
    if (from !== "/app") rememberReturnTo(from);
  }, [from]);
  const oidc = isOidcConfigured();
  const [submitting, setSubmitting] = useState(false);

  // Single-shot guard — initiateOidcRedirect() must fire at most once
  // per mount even if React 19's effect re-entry triggers a re-render.
  // Without it, oidc-client-ts's PKCE state in sessionStorage gets
  // clobbered mid-flight (see ADR 2026-05-29 sub-pivot §6 history).
  const redirectFiredRef = useRef(false);

  // External-IdP (Google/Apple) failure bounce: the backend's failureUrl
  // and /auth/idp-callback's error path both return here with ?idp_error=1.
  // Surface it once so the user knows the SSO round-trip didn't complete.
  const idpErrorShownRef = useRef(false);
  useEffect(() => {
    if (idpErrorShownRef.current) return;
    if (searchParams.get("idp_error")) {
      idpErrorShownRef.current = true;
      pushToast(
        "error",
        "Sign-in with that provider didn't complete. Please try again.",
      );
    }
  }, [searchParams, pushToast]);

  // Keeps an authRequest shown here signed out. Twin starts its own only then (the redirect effect below), so once the
  // tab has a Twin session the last one kept is Twin's, even if another client's was kept before it.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && authRequestId) rememberOwnAuthRequest(authRequestId);
  }, [isLoading, isAuthenticated, authRequestId]);

  // Already-signed-in short-circuit, except for another client's authRequest. This page is the login UI of the whole
  // Zitadel instance, so Foundry Scope (microvision-web) sends a person with a Twin session here with its own; leaving
  // for the Twin page would never finish it, so the form stays (Google is one click). Twin's own authRequest still goes
  // on to the page, as on a Back after the sign-in. Twin's sign-in from the form never needs this: it sends the window
  // to the callback (handleSubmit below).
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated && (!authRequestId || isOwnAuthRequest(authRequestId))) {
      // Signed in already: straight to the page, so no kept address is left for a later /app (#1211 review, LOW 1).
      clearReturnTo();
      navigate(from, { replace: true });
    }
  }, [isLoading, isAuthenticated, authRequestId, navigate, from]);

  // Loop-termination: in OIDC mode, if we lack an authRequest, fire
  // signinRedirect once so Zitadel mints one and 302s back here.
  useEffect(() => {
    if (!oidc) return;
    if (isLoading || isAuthenticated) return;
    if (authRequestId) return; // we have one — render the form
    if (redirectFiredRef.current) return;
    redirectFiredRef.current = true;
    initiateOidcRedirect();
  }, [oidc, isLoading, isAuthenticated, authRequestId, initiateOidcRedirect]);

  // Render placeholder while waiting for Zitadel to redirect back
  // with ?authRequest. In stub mode this branch is skipped entirely.
  if (oidc && !authRequestId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface px-6 py-12 text-on-surface">
        <BrandMark variant="full" className="h-12" />
        <p className="text-sm text-on-surface-muted">
          {isAuthenticated
            ? "Already signed in — taking you home…"
            : isLoading
              ? "Loading session…"
              : "Preparing sign-in…"}
        </p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    if (!email || !password) {
      pushToast("error", "Email and password are required.");
      return;
    }

    setSubmitting(true);
    // In stub mode the backend isn't reachable; authRequestId is
    // ignored by the synthetic stub signIn so any sentinel value
    // works.
    const result = await signIn(email, password, authRequestId ?? "stub");
    setSubmitting(false);

    if (result.ok) {
      // window navigation, NOT navigate() — in OIDC mode callbackUrl
      // is on Zitadel's origin and carries ?code=&state= for the PKCE
      // exchange on /auth/callback.
      window.location.assign(result.callbackUrl);
      return;
    }
    pushToast("error", authErrorMessage(result.code));
  };

  return (
    <div className="grid min-h-screen bg-surface lg:grid-cols-2">
      {/* Brand hero — hidden on small screens */}
      <aside className="relative hidden overflow-hidden bg-brand text-brand-foreground lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 -top-32 size-[420px] rounded-full bg-brand-foreground/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -right-20 size-[520px] rounded-full bg-brand-foreground/5 blur-3xl"
        />

        <Link
          to="/"
          className="relative flex items-center"
          aria-label={tenant.productName}
        >
          {/* On the brand-coloured hero the dark lockup is always inverted to
              read light, independent of light/dark theme mode. */}
          <BrandMark variant="full" className="h-10 invert dark:invert-0" />
        </Link>

        <div className="relative flex flex-col gap-4">
          <Sparkles className="size-6 opacity-70" />
          <p className="max-w-md text-2xl font-semibold leading-snug">
            Welcome back. Your plant is waiting.
          </p>
          <p className="max-w-md text-sm text-brand-foreground/80">
            Sign in to see live readings, batch traces, and AI casting consultant insights
            from your sites — wherever they are.
          </p>
        </div>

        <p className="relative text-xs text-brand-foreground/70">
          Phase 0 preview · running on dev infrastructure.
        </p>
      </aside>

      {/* Form */}
      <main className="flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-6 py-4 lg:hidden">
          <Link to="/" className="flex items-center" aria-label={tenant.productName}>
            <BrandMark variant="full" className="h-9" />
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center p-6 md:p-12">
          <div className="flex w-full max-w-sm flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-semibold tracking-tight text-on-surface">
                Sign in
              </h1>
              <p className="text-sm text-on-surface-muted">
                Use your work email or one of the providers below.
              </p>
            </div>

            {authRequestId && configuredIdps().length > 0 && (
              <>
                <div className="flex flex-col gap-2">
                  {configuredIdps().map((p) => (
                    <Button
                      key={p.id}
                      variant="outline"
                      type="button"
                      onClick={() => {
                        void startIdpLogin(p.id, authRequestId).catch(() =>
                          pushToast(
                            "error",
                            "Couldn't start sign-in with that provider. Please try again.",
                          ),
                        );
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-on-surface-muted">
                  <Separator className="flex-1" />
                  or with email
                  <Separator className="flex-1" />
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-brand hover:underline"
                  >
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  disabled={submitting}
                />
              </div>

              <Button type="submit" size="lg" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
                {!submitting && <ArrowRight className="ml-1.5 size-4" />}
              </Button>
            </form>

            <p className="text-center text-sm text-on-surface-muted">
              Don't have an account?{" "}
              <Link
                to={authRequestId ? `/signup?authRequest=${authRequestId}` : "/signup"}
                className="font-medium text-brand hover:underline"
              >
                Create one
              </Link>
            </p>

            {!oidc && (
              <p className="rounded-md border border-dashed border-border bg-surface-elevated/50 p-3 text-xs text-on-surface-muted">
                Stub mode — VITE_OIDC_AUTHORITY / VITE_OIDC_CLIENT_ID are
                not set. Submitting this form resolves the dev auth shim
                directly; no Zitadel round-trip.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
